import './App.css'
import './assets/css/style.css'
import 'react-quill/dist/quill.snow.css'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { ToastContextProvider } from './context/ToastContext'
import AuthorizationRoute from './utils/AuthorizationRoute'
import Login from './pages/Login'
import ForgotPassword from 'pages/auth/ForgotPassword'
import ResetPassword from 'pages/auth/ResetPassword'
import SharedLayout from 'utils/SharedLayout'
import CompanyHome from 'pages/CompanyHome'
import EmployeeRequest from 'pages/employee_request/EmployeeRequest'
import DepartmentManager from 'pages/department_manager/DepartmentManager'
import SkillManager from 'pages/skill_manager/SkillManager'
import HROnboardings from 'pages/HR_onboarding/HROnboardings'
import AccountantOnboardings from 'pages/accountant_onboarding/AccountantOnboardings'
import Support from 'pages/support/Support'
import ManageJobs from 'pages/manage_jobs/ManageJobs'
import TotalAcceptedEmployee from 'pages/total_accepted_employee/TotalAcceptedEmployee'
import FormsManager from 'pages/forms_manager/FormsManager'
import BellNotifications from 'pages/notification_manager/BellNotifications'
import Profile from 'pages/profile/Profile'
import SubscriptionUpdated from 'pages/profile/subscriptionDetails/SubscriptionUpdated'
import ChangePassword from 'pages/auth/ChangePassword'
import NotificationManager from 'pages/notification_manager/NotificationManager'
import AddNotification from 'pages/notification_manager/AddNotification'
import HiringView from 'pages/forms_manager/hiring/HiringView'
import AddHiringForm from 'pages/forms_manager/hiring/AddHiringForm'
import HiringEdit from 'pages/forms_manager/hiring/EditHiring'
import OnboardingView from 'pages/forms_manager/onboarding/OnboardingView'
import EditOnboarding from 'pages/forms_manager/onboarding/EditOnboarding'
import AddOnboardingForm from 'pages/forms_manager/onboarding/AddOnboardingForm'
import ChatWidget from './pages/ChatWidget'
import SuggestedForms from 'pages/suggested_forms/SuggestedForms'
import SuggestedHiringView from 'pages/suggested_forms/hiring/HiringView'
import SuggestedOnboardingView from 'pages/suggested_forms/onboarding/OnboardingView'
import { useEffect, useRef, useCallback } from 'react'
import { apiGet } from './utils/apiFetch'
import apiPath from './utils/pathObj'
import DealPipeline from 'pages/pipedrive/Deals.js'
import Lead_Inbox from 'pages/pipedrive/Lead_Inbox.js'
import LeadDetail from 'pages/pipedrive/LeadDetail.js'
import PipedriveConnection from 'pages/pipedrive/PipedriveConnection'
import PayrollOnboardingWizard from 'pages/payroll/PayrollOnboardingWizard'
import PayrollOnboardingPage from 'pages/payroll/PayrollOnboardingPage'

// Auto-logout configuration
const AUTO_LOGOUT_TIME = 60 * 60 * 1000; // 60 minutes for testing
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'click', 'wheel'];
const PUBLIC_ROUTES = ['/', '/login', '/forgot-password', '/company-reset-password'];

// List of all protected routes (inside SharedLayout)
const PROTECTED_ROUTES = [
  '/dashboard',
  '/job_request',
  '/department_manager',
  '/skill_manager',
  '/HR-onboarding',
  '/accountant-onboarding',
  '/support',
  '/manage-jobs',
  '/all-employees',
  '/forms_manager',
  '/forms_manager/addhiring',
  '/forms_manager/view',
  '/forms_manager/hiring-edit',
  '/forms_manager/edit',
  '/forms_manager/addonboarding',
  '/forms_manager/onboarding-edit',
  '/suggested_forms',
  '/suggested_forms/hiring-view',
  '/suggested_forms/onboarding-view',
  '/notification_manager',
  '/notification_manager/add',
  '/notification-list',
  '/profile',
  '/subscription-updated',
  '/change-password',
];

// Wrapper component to handle auto-logout logic
function AppContent({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isAuthRef = useRef(false);

  const isPublicRoute = useCallback((path) => {
    return PUBLIC_ROUTES.some(route => path === route || path.startsWith(route + '/'));
  }, []);

  const isProtectedRoute = useCallback((path) => {
    // Check if the current path matches any protected route
    return PROTECTED_ROUTES.some(route => 
      path === route || path.startsWith(route + '/')
    );
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showNextLogoutTime = useCallback(() => {
    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      // const logoutTime = new Date(Date.now() + AUTO_LOGOUT_TIME).toLocaleTimeString();
      // console.log(`⏰ Next auto-logout at: ${logoutTime}`);
    }
  }, [location.pathname, isProtectedRoute]);

  const handleLogout = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("token");
      isAuthRef.current = false;
      clearTimer();
      navigate('/login');
    }
  }, [navigate, clearTimer]);

  const resetTimer = useCallback(() => {
    if (!isAuthRef.current) {
      return;
    }
    
    if (!isProtectedRoute(location.pathname)) {
      return;
    }
    
    clearTimer();
    
    timerRef.current = setTimeout(() => {
      if (isProtectedRoute(location.pathname) && isAuthRef.current) {
        handleLogout();
      }
    }, AUTO_LOGOUT_TIME);
    
    showNextLogoutTime();
  }, [clearTimer, location.pathname, isProtectedRoute, showNextLogoutTime, handleLogout]);

  const handleActivity = useCallback((event) => {
    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      lastActivityRef.current = Date.now();
      resetTimer();
    }
  }, [resetTimer, location.pathname, isProtectedRoute]);

  // Check authentication status on mount and route changes
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    isAuthRef.current = !!token;
    
    // Clean up previous listeners
    ACTIVITY_EVENTS.forEach(e => {
      window.removeEventListener(e, handleActivity);
    });
    
    if (isAuthRef.current && isProtectedRoute(location.pathname)) {
      lastActivityRef.current = Date.now();
      resetTimer();
      
      // Set up activity listeners
      ACTIVITY_EVENTS.forEach(e => {
        window.addEventListener(e, handleActivity);
      });
      
      // Handle tab visibility change
      const handleVisibilityChange = () => {
        if (!document.hidden && isAuthRef.current && isProtectedRoute(location.pathname)) {
          const timeAway = Date.now() - lastActivityRef.current;
          
          if (timeAway > AUTO_LOGOUT_TIME) {
            handleLogout();
          } else {
            resetTimer();
          }
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Store for cleanup
      return () => {
        clearTimer();
        ACTIVITY_EVENTS.forEach(e => {
          window.removeEventListener(e, handleActivity);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else if (isAuthRef.current && !isProtectedRoute(location.pathname)) {
      clearTimer();
      return () => {
        clearTimer();
      };
    } else {
      clearTimer();
      return () => {
        clearTimer();
      };
    }
  }, [location.pathname, resetTimer, handleActivity, clearTimer, handleLogout, isPublicRoute, isProtectedRoute]);

  // GEO ACCESS: ping while logged in so a missing/changed IP expires the session
  // without waiting for a click. skipLoader avoids the stuck Loading overlay.
  useEffect(() => {
    if (isPublicRoute(location.pathname)) return undefined;
    const ping = () => {
      if (!sessionStorage.getItem("token")) return;
      apiGet(apiPath.getDashboardDetails, {}, { skipLoader: true }).catch(() => {});
    };
    if (!sessionStorage.getItem("token")) return undefined;
    ping();
    const id = setInterval(ping, 1800000);
    return () => clearInterval(id);
  }, [location.pathname, isPublicRoute]);

  return children;
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ToastContextProvider>
        <AuthProvider>
          <AppContent>
            <Routes>
              <Route element={<SharedLayout />}>
                <Route exact path='/dashboard' element={<CompanyHome />} />
                <Route exact path='/job_request' element={<AuthorizationRoute><EmployeeRequest /></AuthorizationRoute>} />
                <Route exact path='/department_manager' element={<AuthorizationRoute><DepartmentManager /></AuthorizationRoute>} />
                <Route exact path='/skill_manager' element={<AuthorizationRoute><SkillManager /></AuthorizationRoute>} />
                <Route exact path='/HR-onboarding' element={<AuthorizationRoute><HROnboardings /></AuthorizationRoute>} />
                <Route exact path='/accountant-onboarding' element={<AuthorizationRoute><AccountantOnboardings /></AuthorizationRoute>} />
                <Route exact path='/support' element={<AuthorizationRoute><Support /></AuthorizationRoute>} />
                <Route exact path='/manage-jobs' element={<AuthorizationRoute><ManageJobs /></AuthorizationRoute>} />
                <Route exact path='/all-employees' element={<AuthorizationRoute><TotalAcceptedEmployee /></AuthorizationRoute>} />
                <Route exact path='/forms_manager' element={<AuthorizationRoute><FormsManager /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/addhiring' element={<AuthorizationRoute><AddHiringForm /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/view' element={<AuthorizationRoute><HiringView /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/hiring-edit' element={<AuthorizationRoute><HiringEdit /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/edit' element={<AuthorizationRoute><OnboardingView /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/addonboarding' element={<AuthorizationRoute><AddOnboardingForm /></AuthorizationRoute>} />
                <Route exact path='/forms_manager/onboarding-edit' element={<AuthorizationRoute><EditOnboarding /></AuthorizationRoute>} />
                <Route exact path='/suggested_forms' element={<AuthorizationRoute><SuggestedForms /></AuthorizationRoute>} />
                <Route exact path='/suggested_forms/hiring-view' element={<AuthorizationRoute><SuggestedHiringView /></AuthorizationRoute>} />
                <Route exact path='/suggested_forms/onboarding-view' element={<AuthorizationRoute><SuggestedOnboardingView /></AuthorizationRoute>} />
                <Route exact path='/notification_manager' element={<AuthorizationRoute><NotificationManager /></AuthorizationRoute>} />
                <Route exact path='/notification_manager/add' element={<AuthorizationRoute><AddNotification /></AuthorizationRoute>} />
                <Route exact path='/notification-list' element={<AuthorizationRoute><BellNotifications /></AuthorizationRoute>} />
                <Route exact path='/profile' element={<AuthorizationRoute><Profile /></AuthorizationRoute>} />
                <Route exact path='/subscription-updated' element={<AuthorizationRoute><SubscriptionUpdated /></AuthorizationRoute>} />
                <Route exact path='/change-password' element={<AuthorizationRoute><ChangePassword /></AuthorizationRoute>} />
                <Route exact path='/pipedrive/deal-pipeline' element={<AuthorizationRoute><DealPipeline /></AuthorizationRoute>} />
                <Route exact path='/pipedrive/leads-inbox' element={<AuthorizationRoute><Lead_Inbox /></AuthorizationRoute>} />
                <Route exact path='/pipedrive/leads-inbox/:id' element={<AuthorizationRoute><LeadDetail /></AuthorizationRoute>} />
                <Route exact path='/pipedrive/deal-pipeline/:id' element={<AuthorizationRoute><LeadDetail /></AuthorizationRoute>} />
                <Route exact path='/pipedrive-connection' element={<AuthorizationRoute><PipedriveConnection /></AuthorizationRoute>} />
                <Route exact path='/payroll-onboarding' element={<AuthorizationRoute><PayrollOnboardingPage /></AuthorizationRoute>} />
              </Route>
              <Route exact path='/' element={<Login />} />
              <Route exact path='/login' element={<Login />} />
              <Route path='/forgot-password' element={<ForgotPassword />} />
              <Route exact path='/company-reset-password/:resetToken' element={<ResetPassword />} />
            </Routes>
            {/*<AuthorizationRoute>
              <ChatWidget />
            </AuthorizationRoute>*/}
          </AppContent>
        </AuthProvider>
      </ToastContextProvider>
    </BrowserRouter>
  )
}

export default App